// SettingsDocumentsPane.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Services
import { updateBase } from 'services/bases';
import {
  buildDocumentLetterheadSettingsPatch,
  documentLetterheadSettingsEqual,
  getDocumentLetterheadSettings,
} from 'services/bases/documentBaseSettings';

// CSS
import './SettingsPurchaseOrdersPane.css';
import './SettingsNamePane.css';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';
import NodeLocationField from 'node/form/fields/location/NodeLocationField';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';

/**
 * SettingsDocumentsPane
 *
 * Shared letterhead (phone + address) for Proposal, Purchase Order, Invoice, etc.
 */
const SettingsDocumentsPane = React.memo(({
  theme,
  base = null,
  canEditBase = false,
}) => {
  // ----- CONTEXT ----- //

  const { setActiveBase } = useBase();

  // ----- STATE ----- //

  const persisted = useMemo(() => getDocumentLetterheadSettings(base), [base]);

  const [phoneValue, setPhoneValue] = useState(persisted.phone);
  const [addressValue, setAddressValue] = useState(persisted.address);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setPhoneValue(persisted.phone);
    setAddressValue(persisted.address);
  }, [persisted]);

  const draft = useMemo(() => ({
    phone: phoneValue,
    address: addressValue,
  }), [addressValue, phoneValue]);

  const hasChanges = !documentLetterheadSettingsEqual(draft, persisted);
  const controlsDisabled = !canEditBase || isSaving;

  // ----- HANDLERS ----- //

  const clearStatus = () => {
    setErrorMessage('');
    setStatusMessage('');
  };

  const handlePhoneChange = (event) => {
    setPhoneValue(event?.target?.value || '');
    clearStatus();
  };

  const handleAddressSaved = (nextAddress) => {
    setAddressValue(nextAddress || null);
    clearStatus();
  };

  const handleSave = async () => {
    if (isSaving || !base?.key || !canEditBase) return;

    setErrorMessage('');
    setStatusMessage('Saving...');
    setIsSaving(true);

    const patch = buildDocumentLetterheadSettingsPatch(draft, base?.documents);

    try {
      await updateBase(base.key, patch);
      setActiveBase((previous) => {
        if (!previous || previous.key !== base.key) return previous;
        return {
          ...previous,
          documents: patch.documents,
        };
      });
      setStatusMessage('Document letterhead updated.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to update document letterhead.');
      setStatusMessage('');
    } finally {
      setIsSaving(false);
    }
  };

  // ----- RENDER ----- //

  return (
    <>
      <SettingsModalPaneHeader
        title="Documents"
        subtitle="Shared letterhead for proposals, purchase orders, invoices, and other documents. Icon and company name come from About."
      />

      <SettingsModalPaneContents>
        <div className="settings-purchase-orders-fields">
          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Phone
            </div>

            <div
              className="settings-purchase-orders-phone-card"
              style={{
                borderColor: theme.backgroundHover,
              }}
            >
              <TextInput
                value={phoneValue}
                disabled={controlsDisabled}
                maxLength={40}
                width="100%"
                placeholder="+1…"
                onChange={handlePhoneChange}
                onEnterPress={hasChanges ? handleSave : undefined}
              />
            </div>
          </div>

          <div className="settings-purchase-orders-field">
            <div className="settings-modal-field-label">
              Address
            </div>

            <NodeLocationField
              value={addressValue}
              readOnly={controlsDisabled}
              onLocationSaved={canEditBase ? handleAddressSaved : undefined}
            />
          </div>

          {canEditBase ? (
            <div className="settings-purchase-orders-actions">
              <Button
                iconKey="save"
                label="Save"
                enabled={hasChanges && !isSaving}
                onClick={handleSave}
              />
            </div>
          ) : null}
        </div>

        {!canEditBase && (
          <div
            className="settings-modal-note"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            Only admins and owners can update document letterhead.
          </div>
        )}

        {statusMessage && !errorMessage ? (
          <div className="settings-modal-status">{statusMessage}</div>
        ) : null}

        {errorMessage ? (
          <div className="settings-modal-error">{errorMessage}</div>
        ) : null}
      </SettingsModalPaneContents>
    </>
  );
});

SettingsDocumentsPane.displayName = 'SettingsDocumentsPane';

export default SettingsDocumentsPane;
